/**
 * Lazy Loading Slideshow - Flickity Integration
 * Only initializes slideshows when they become visible
 */

class SlideshowLazy {
  constructor() {
    this.initialized = new Set();
    this.flickityLoaded = false;
    this.pendingSlideshows = [];
    
    this.init();
  }

  async init() {
    try {
      // Load Flickity script first
      await this.loadFlickity();
      
      // Setup intersection observer for lazy loading
      this.setupObserver();
      
      // Process any slideshows that are already visible
      this.processPendingSlideshows();
    } catch (error) {
      console.error('[SlideshowLazy] Initialization failed:', error);
    }
  }

  loadFlickity() {
    return new Promise((resolve) => {
      if (window.Flickity) {
        this.flickityLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = window.slideshowAssets?.flickityJs || '/assets/flickity.min.js';
      script.async = true;
      
      script.onload = () => {
        this.flickityLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        console.warn('Failed to load Flickity. Slideshows will not be interactive.');
        resolve();
      };
      
      document.head.appendChild(script);
    });
  }

  setupObserver() {
    // Check if IntersectionObserver is supported
    if (!window.IntersectionObserver) {
      this.fallbackInit();
      return;
    }

    const options = {
      root: null,
      rootMargin: '50px 0px', // Start loading 50px before entering viewport
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.initializeSlideshow(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, options);

    // Observe all slideshow carousels
    const slideshows = document.querySelectorAll('.slideshow-advanced__carousel[data-flickity]');
    slideshows.forEach(slideshow => {
      this.observer.observe(slideshow);
    });
  }

  fallbackInit() {
    // Fallback for browsers without IntersectionObserver
    // Initialize all slideshows immediately
    const slideshows = document.querySelectorAll('.slideshow-advanced__carousel[data-flickity]');
    slideshows.forEach(slideshow => {
      this.initializeSlideshow(slideshow);
    });
  }

  initializeSlideshow(carousel) {
    if (this.initialized.has(carousel) || !window.Flickity) {
      return;
    }

    try {
      // Get Flickity options from data attribute
      const flickityData = carousel.getAttribute('data-flickity');
      const options = flickityData ? JSON.parse(flickityData) : {};
      
      // Initialize Flickity
      const flickity = new window.Flickity(carousel, options);

      // Mark as initialized
      this.initialized.add(carousel);

      // Fix accessibility: manage tabindex for focusable elements in hidden slides
      this.manageFocusableElements(flickity, carousel);

      // Dispatch custom event for tracking
      carousel.dispatchEvent(new CustomEvent('slideshowLazyLoaded', {
        detail: { flickity }
      }));


    } catch (error) {
      console.error('Failed to initialize slideshow:', error);
    }
  }

  processPendingSlideshows() {
    // Process any slideshows that were queued before Flickity loaded
    this.pendingSlideshows.forEach(carousel => {
      this.initializeSlideshow(carousel);
    });
    this.pendingSlideshows = [];
  }

  manageFocusableElements(flickity, carousel) {
    // Fix accessibility: prevent focus on hidden slides
    const updateFocusableElements = () => {
      const slides = carousel.querySelectorAll('.slideshow-advanced__slide');

      slides.forEach((slide, index) => {
        const isSelected = index === flickity.selectedIndex;
        const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');

        // Set aria-hidden based on slide visibility
        slide.setAttribute('aria-hidden', isSelected ? 'false' : 'true');

        // Manage tabindex for focusable elements
        focusableElements.forEach(element => {
          if (isSelected) {
            // Active slide: restore original tabindex
            const originalTabindex = element.getAttribute('data-original-tabindex');
            if (originalTabindex !== null) {
              element.setAttribute('tabindex', originalTabindex);
              element.removeAttribute('data-original-tabindex');
            } else {
              element.removeAttribute('tabindex');
            }
          } else {
            // Hidden slide: save original and set to -1
            if (!element.hasAttribute('data-original-tabindex')) {
              const currentTabindex = element.getAttribute('tabindex');
              if (currentTabindex !== null) {
                element.setAttribute('data-original-tabindex', currentTabindex);
              } else {
                element.setAttribute('data-original-tabindex', '0');
              }
            }
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    };

    // Initial update
    updateFocusableElements();

    // Update on slide change
    flickity.on('change', updateFocusableElements);
  }

  // Public method to manually initialize a slideshow
  forceInit(carousel) {
    if (typeof carousel === 'string') {
      carousel = document.querySelector(carousel);
    }
    
    if (carousel && this.flickityLoaded) {
      this.initializeSlideshow(carousel);
    }
  }

  // Clean up method
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.initialized.clear();
  }
}

// Auto-initialize when DOM is ready
function initSlideshowLazy() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.slideshowLazy) {
        window.slideshowLazy = new SlideshowLazy();
      }
    });
  } else {
    if (!window.slideshowLazy) {
      window.slideshowLazy = new SlideshowLazy();
    }
  }
}

// Initialize
initSlideshowLazy();

// Export for potential manual use
if (typeof window !== 'undefined') {
  window.SlideshowLazy = SlideshowLazy;
}